import { caseDbApiEndpoint } from '../utils/apiOrigin';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';

const RECEIPT_ID_PATTERN = /^[a-f0-9]{32}$/;
const OPERATION_LABELS = new Set(['copy', 'move', 'delete']);
const PUBLIC_STATUSES = new Set([
    'available',
    'complete',
    'completed',
    'pending',
    'running',
    'needs_review',
    'undoing',
    'undone',
    'failed',
    'expired',
]);
const INTERNAL_PENDING_STATUSES = new Set(['prepared', 'deleting', 'db_pending']);

const requireScope = ({ caseId, userId }) => {
    const normalizedCaseId = String(caseId ?? '').trim();
    const normalizedUserId = String(userId ?? '').trim();
    if (!/^\d+$/.test(normalizedCaseId) || !/^\d+$/.test(normalizedUserId)) {
        throw new Error('The current document workspace could not be verified.');
    }
    return { caseId: normalizedCaseId, userId: normalizedUserId };
};

const readPayload = async (response) => {
    const payload = await response.json().catch(() => null);
    // These calls can mutate stored documents. A generic 2xx response is not
    // proof that the server committed or resumed the requested operation.
    // Require the endpoint's explicit success acknowledgement so an empty,
    // proxied, or otherwise malformed response never becomes a false undo.
    if (!response.ok || !payload || payload.success !== true) {
        const error = new Error(
            payload?.message || payload?.error || 'Recent document actions are unavailable.'
        );
        error.status = response.status;
        error.code = payload?.code || payload?.error_code || '';
        throw error;
    }
    return payload;
};

const normalizeTimestamp = (value) => {
    if (typeof value !== 'string' || !value.trim()) return null;
    return value.trim().slice(0, 64);
};

export const normalizeDocumentOperation = (operation) => {
    if (!operation || typeof operation !== 'object' || Array.isArray(operation)) {
        throw new Error('Recent document actions returned an invalid item.');
    }

    const receiptId = String(operation.receipt_id ?? '').trim();
    const operationName = String(operation.operation ?? '').trim().toLowerCase();
    const receivedStatus = String(operation.status ?? '').trim().toLowerCase();
    const status = INTERNAL_PENDING_STATUSES.has(receivedStatus) ? 'pending' : receivedStatus;
    const objectCount = Number(operation.object_count ?? 0);
    if (!RECEIPT_ID_PATTERN.test(receiptId)
        || !OPERATION_LABELS.has(operationName)
        || !PUBLIC_STATUSES.has(status)
        || !Number.isSafeInteger(objectCount)
        || objectCount < 0) {
        throw new Error('Recent document actions returned an invalid item.');
    }

    // Deliberately allowlist public summary fields. Tokens, object keys, and
    // internal payloads must never reach component state or the DOM.
    return {
        receiptId,
        operation: operationName,
        objectCount,
        status,
        createdAt: normalizeTimestamp(operation.created_at),
        expiresAt: normalizeTimestamp(operation.expires_at),
        canUndo: operation.can_undo === true,
        canResume: operation.can_resume === true,
        cleanupPending: operation.cleanup_pending === true,
        resumeLabel: operation.resume_label === 'Resume undo' || status === 'undoing'
            ? 'Resume undo'
            : 'Resume',
    };
};

const request = async (scope, body, options = {}) => {
    const verified = requireScope(scope);
    if (options.signal?.aborted) throw new DOMException('Request canceled', 'AbortError');
    const csrfToken = await ensureCaseDbCsrfToken({ expectedUserId: verified.userId });
    if (options.signal?.aborted) throw new DOMException('Request canceled', 'AbortError');
    if (!csrfToken) {
        const error = new Error('Your session could not be verified. Sign in again and retry.');
        error.status = 401;
        throw error;
    }

    const response = await (options.fetchImpl || fetch)(
        options.endpoint || caseDbApiEndpoint('s3_upload.php'),
        {
            method: 'PUT',
            credentials: 'include',
            cache: 'no-store',
            signal: options.signal,
            headers: withCsrfHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
            }, 'PUT'),
            body: JSON.stringify({ ...body, case_id: verified.caseId }),
        }
    );
    return readPayload(response);
};

export const listDocumentOperations = async (scope, options = {}) => {
    const payload = await request(scope, { action: 'list_document_operations' }, options);
    if (!Array.isArray(payload.operations)) {
        throw new Error('Recent document actions returned an invalid response.');
    }
    return payload.operations.map(normalizeDocumentOperation);
};

export const undoDocumentOperation = async (scope, receiptId, options = {}) => {
    const normalizedReceiptId = String(receiptId ?? '').trim();
    if (!RECEIPT_ID_PATTERN.test(normalizedReceiptId)) {
        throw new Error('That document action could not be verified.');
    }
    return request(scope, {
        action: 'undo_document_operation',
        receipt_id: normalizedReceiptId,
    }, options);
};

export const resumeDocumentOperation = async (scope, receiptId, options = {}) => {
    const normalizedReceiptId = String(receiptId ?? '').trim();
    if (!RECEIPT_ID_PATTERN.test(normalizedReceiptId)) {
        throw new Error('That document action could not be verified.');
    }
    return request(scope, {
        action: 'resume_document_operation',
        receipt_id: normalizedReceiptId,
    }, options);
};

export const documentOperationScopeKey = ({ caseId, userId }) => (
    JSON.stringify([String(caseId ?? ''), String(userId ?? '')])
);
